import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { UserRole } from "@/lib/navigation";
import { Button } from "@/components/ui/button";

interface Props {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

export default function ProtectedRoute({ children, allowedRoles }: Props) {
  const { user, role, loading, signOut } = useAuth();

  // Petit délai de grâce : le rôle est chargé en parallèle de l'auth.
  // On évite d'afficher "Aucun rôle attribué" pendant cette fenêtre courte.
  const [graceElapsed, setGraceElapsed] = useState(false);
  useEffect(() => {
    if (!user || role) return;
    const t = setTimeout(() => setGraceElapsed(true), 2500);
    return () => clearTimeout(t);
  }, [user, role]);

  if (loading || (user && !role && !graceElapsed)) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!role) {
    return (
      <div className="flex h-screen items-center justify-center bg-background flex-col gap-4">
        <p className="text-muted-foreground">Aucun rôle attribué. Contactez un administrateur.</p>
        <Button variant="outline" size="sm" onClick={signOut}>Se déconnecter</Button>
      </div>
    );
  }
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}
